import { useWizardSessionIdContext } from '@/hooks/WizardSessionIdContext';

/**
 * Small debug line surfacing the wizard session id on every wizard
 * page. Rendered by `RegistrationLayout` after `<Outlet>`, so it sits
 * below whatever step content + footer (incl. the "Continua" button)
 * the active step renders.
 *
 * Why
 * ---
 * During dev/test the user needs a stable id to quote when reporting
 * a problem mid-wizard. The sid no longer pollutes the URL (after
 * fix/onboarding-fe-wizard-session-id-no-url-sync), so without an
 * on-page surface the user has no easy way to read it.
 *
 * Interaction
 * -----------
 * The id span is `select-all` — single-click selects the whole UUID
 * for Ctrl+C. No raw <button> primitive needed (rejected by the
 * `fe-gov/no-raw-html-primitives` lint), no design-arbitrary text
 * sizes (rejected by `fe-gov/tw-no-design-arbitrary`).
 *
 * Placement
 * ---------
 * Mounted once in `RegistrationLayout`, below the Outlet. Appears on:
 *   /organizzazione, /contatti, /indirizzi, /amministratore, /fiscale,
 *   /riepilogo, /documenti, /success
 *
 * If the testing phase concludes, deletion is one-line: drop the
 * component import + JSX usage in RegistrationLayout. The hook
 * (`useWizardSessionId`) and the context provider stay, since the
 * sid is still passed to BE calls for log correlation regardless.
 */
export function WizardSessionDebugLine(): JSX.Element | null {
  const sid = useWizardSessionIdContext();
  if (!sid) return null;
  return (
    <div className="mx-auto mt-4 flex w-full max-w-md items-center justify-center gap-2 px-4 text-xs text-muted-foreground">
      <span className="select-none">ID sessione</span>
      <span
        className="select-all break-all font-mono text-foreground/80"
        aria-label="ID sessione di test (clicca per selezionare)"
      >
        {sid}
      </span>
    </div>
  );
}
